import * as Api from '@ViewModels';
import { toast } from 'sonner';
import {
	IRegEdSubmissionCreateRequest,
	IRegEdSubmissionMetadata,
	IRegEdSubmissionView,
	RegEdComplianceContentType,
} from './types';

export interface ISubmitRegEdComplianceArgs {
	userSession: Api.UserSessionContext;
	parentType: RegEdComplianceContentType;
	parentId: string | undefined;
	/** From the created parent's `approvalRequests[0].id`; falsy means the server built none to file against. */
	approvalRequestId: string | null | undefined;
	metadata: IRegEdSubmissionMetadata | null | undefined;
	revisesApprovalRequestId?: string | null;
	impersonationContext?: Api.IImpersonationContext;
	logApiError?: (action: string, error?: Api.IOperationResultNoValue) => void;
	/** Failure-toast verb — the parent was already 'scheduled' (default) or 'sent'. */
	action?: 'scheduled' | 'sent';
	/** How to surface the resubmit message; defaults to a sonner toast (admin flows pass errorMessages.push). */
	onError?: (message: string) => void;
}

/**
 * The 2-step RegEd submission POST (`compliance/regEd/submissions`), isolated so a failure never fails
 * the already-created parent. Returns true when the filing succeeded or there was nothing to file;
 * false when it was dropped (no approval request) or errored — both emit `RegEdSubmission-*` telemetry
 * and a resubmit message so ops keep signal and the advisor is told to reopen and resubmit.
 */
export const submitRegEdCompliance = async ({
	userSession,
	parentType,
	parentId,
	approvalRequestId,
	metadata,
	revisesApprovalRequestId,
	impersonationContext,
	logApiError,
	action = 'scheduled',
	onError,
}: ISubmitRegEdComplianceArgs): Promise<boolean> => {
	if (!metadata) {
		return true;
	}
	const noun = parentType === RegEdComplianceContentType.Email ? 'campaign' : 'post';
	const surface = onError ?? ((message: string) => toast.error(message));
	const resubmitMessage = `${action === 'sent' ? 'Sent' : 'Scheduled'}, but the compliance submission didn’t go through — reopen the ${noun} to resubmit.`;

	if (!parentId || !approvalRequestId) {
		logApiError?.('RegEdSubmission-MissingApprovalRequest');
		surface(resubmitMessage);
		return false;
	}

	const submissionRequest: IRegEdSubmissionCreateRequest = {
		parentType,
		parentId,
		approvalRequestId,
		metadata,
		revisesApprovalRequestId: revisesApprovalRequestId ?? null,
	};
	try {
		await userSession.webServiceHelper.callAsync<IRegEdSubmissionView>(
			Api.ImpersonationBroker.composeApiUrl({ urlPath: 'compliance/regEd/submissions', impersonationContext }),
			'POST',
			submissionRequest
		);
		return true;
	} catch (submissionError) {
		logApiError?.('RegEdSubmission-Error', Api.asApiError(submissionError));
		surface(resubmitMessage);
		return false;
	}
};
